import { EObservationTimelineChange } from '@/composables/observationTimeline/observationTimelineQueryInvalidation';
import { ObservationTimelineEntryType } from '@/api/generated/api.enum';
import { Api } from '@/api/api.client';
import type { Components } from '@/api/generated/api';
import { useToast } from '@/composables/useToast';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { runBulk } from 'common/utils/function.utils';
import { invalidatePerformanceAssessmentEntryQueries } from './performanceAssessmentEntryQueryInvalidation';

interface IStudentEntryUpdate {
  schoolClassId: number;
  studentEntryId: number;
  performanceAssessmentEntryId: number;
  dto: Components.Schemas.UpdatePerformanceAssessmentStudentEntryDto;
}

interface ISyncStudentEntries {
  schoolClassId: number;
  performanceAssessmentEntryId: number;
  addedStudentIds: number[];
  removedStudentEntryIds: number[];
}

/** Student rating and recipient actions need no class-wide student-entry query. */
export function usePerformanceAssessmentStudentEntryMutations() {
  const queryClient = useQueryClient();
  const { editSuccessToast, showError } = useToast();
  const onSuccess = async (_data: void, { schoolClassId }: { schoolClassId: number }): Promise<void> => {
    await invalidatePerformanceAssessmentEntryQueries(queryClient, schoolClassId);
    editSuccessToast();
  };
  const onRatingEdited = async (
    _data: void,
    { schoolClassId, performanceAssessmentEntryId }: Omit<IStudentEntryUpdate, 'studentEntryId'>,
  ): Promise<void> => {
    await invalidatePerformanceAssessmentEntryQueries(queryClient, schoolClassId, {
      kind: EObservationTimelineChange.Update,
      entity: { type: ObservationTimelineEntryType.performanceAssessmentEntry, id: performanceAssessmentEntryId },
    });
    editSuccessToast();
  };
  const onError = (error: Error): void => showError(error);
  const update = useMutation({
    mutationFn: async ({ studentEntryId, dto }: IStudentEntryUpdate) => {
      await Api.Routes.PerformanceAssessmentStudentEntry.update({ id: studentEntryId }, dto);
    },
    onSuccess: onRatingEdited,
    onError,
  });
  const bulkUpdate = useMutation({
    mutationFn: async ({
      studentEntryIds,
      dto,
    }: Omit<IStudentEntryUpdate, 'studentEntryId'> & { studentEntryIds: number[] }) => {
      await runBulk(
        studentEntryIds,
        async (id) => {
          await Api.Routes.PerformanceAssessmentStudentEntry.update({ id }, dto);
        },
        { concurrency: 8 },
      );
    },
    onSuccess: onRatingEdited,
    onError,
  });
  const sync = useMutation({
    mutationFn: async ({
      performanceAssessmentEntryId,
      addedStudentIds,
      removedStudentEntryIds,
    }: ISyncStudentEntries) => {
      if (addedStudentIds.length) {
        await Api.Routes.PerformanceAssessmentStudentEntry.createBulk(null, {
          performanceAssessmentEntryId,
          studentIds: addedStudentIds,
        });
      }
      await runBulk(
        removedStudentEntryIds,
        async (id) => {
          await Api.Routes.PerformanceAssessmentStudentEntry.delete({ id });
        },
        { concurrency: 8 },
      );
    },
    onSuccess,
    onError,
  });

  return {
    updatePerformanceAssessmentStudentEntry: update.mutateAsync,
    bulkUpdatePerformanceAssessmentStudentEntries: bulkUpdate.mutateAsync,
    syncPerformanceAssessmentStudentEntries: sync.mutateAsync,
  };
}
